import React, { useMemo, useState, useRef, useEffect } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import type { DateSelectArg, EventClickArg, EventDropArg } from '@fullcalendar/core'
import { Card, CardContent } from 'components/ui/Card'
import { Button } from 'components/ui/Button'
import { Badge } from 'components/ui/Badge'
import { Input } from 'components/ui/Input'
import { CustomSelect } from 'components/ui/CustomSelect'
import { useTranslation } from 'react-i18next'
import { Calendar, X, Search, Clock, Users, List, Grid, MapPin, ChevronLeft, ChevronRight, Link as LinkIcon, ChevronDown, Check } from 'lucide-react'
import { cn } from 'lib/utils'
import { InterviewDetailsModal } from './InterviewDetailsModal'
import { PageHeader } from 'components/layout/PageHeader'

export interface InterviewEvent {
  id: string | number
  title: string
  start: string | Date
  end?: string | Date
  candidateId?: number
  candidateName?: string
  applicationId?: number
  stepOrder?: number
  stepName?: string
  location?: string
  meetingLink?: string
  notes?: string
  interviewerName?: string
  color?: string
  extendedProps?: {
    candidateId?: number
    candidateName?: string
    applicationId?: number
    stepOrder?: number
    stepName?: string
    location?: string
    meetingLink?: string
    notes?: string
    interviewerName?: string
    offerId?: number
    offerTitle?: string
  }
}

interface Step {
  id: number
  name: string
  order: number
  color?: string
  step_type?: string // Optional for filtering steps in legend
}

interface Group {
  id: number
  name: string
  color?: string
}

interface Offer {
  id: number
  name: string
}

interface InterviewCalendarProps {
  events: InterviewEvent[]
  loading?: boolean
  onSlotSelect?: (arg: DateSelectArg) => void
  onEventClick?: (arg: EventClickArg) => void
  onEventDrop?: (arg: EventDropArg) => void
  candidates?: Array<{ id: number; name: string }>
  steps?: Step[]
  groups?: Group[]
  candidateGroups?: Map<number, string[]> // Map of candidateId to group names
  offers?: Offer[] // Optional: offers for filtering
  offerFilter?: number[] | 'all' // Optional: current offer filter (array for multi-select)
  onOfferFilterChange?: (offerIds: number[] | 'all') => void // Optional: callback for offer filter change
  hideHeader?: boolean
}

export const InterviewCalendar: React.FC<InterviewCalendarProps> = ({
  events,
  loading = false,
  onSlotSelect,
  onEventClick,
  onEventDrop,
  candidates = [],
  steps = [],
  groups = [],
  candidateGroups = new Map(),
  offers = [],
  offerFilter: propOfferFilter = 'all',
  onOfferFilterChange,
  hideHeader = false,
}) => {
  const { t, i18n } = useTranslation()
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [view] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'>('timeGridWeek')
  const [candidateFilter, setCandidateFilter] = useState<number | 'all'>('all')
  const [stepFilter, setStepFilter] = useState<number | 'all'>('all')
  const [groupFilter, setGroupFilter] = useState<string | 'all'>('all')
  const [offerFilter, setOfferFilter] = useState<number[] | 'all'>(
    Array.isArray(propOfferFilter) ? propOfferFilter : (propOfferFilter === 'all' ? 'all' : [propOfferFilter])
  )
  const [offerFilterOpen, setOfferFilterOpen] = useState(false)
  const [stepFilterOpen, setStepFilterOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedInterview, setSelectedInterview] = useState<InterviewEvent | null>(null)
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const offerFilterRef = useRef<HTMLDivElement>(null)
  const stepFilterRef = useRef<HTMLDivElement>(null)

  // Sync prop offerFilter with state
  React.useEffect(() => {
    if (propOfferFilter !== undefined) {
      if (Array.isArray(propOfferFilter)) {
        setOfferFilter(propOfferFilter)
      } else if (propOfferFilter === 'all') {
        setOfferFilter('all')
      } else {
        setOfferFilter([propOfferFilter])
      }
    }
  }, [propOfferFilter])

  // Close offer filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (offerFilterRef.current && !offerFilterRef.current.contains(event.target as Node)) {
        setOfferFilterOpen(false)
      }
      if (stepFilterRef.current && !stepFilterRef.current.contains(event.target as Node)) {
        setStepFilterOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Get day names translations for FullCalendar locale
  const calendarLocale = useMemo(() => {
    const currentLang = i18n.language || 'en'
    const isFrench = currentLang.startsWith('fr')
    
    return {
      code: currentLang,
      week: {
        dow: 1, // Monday as first day
        doy: 4
      },
      buttonText: {
        prev: '',
        next: '',
        today: t('offerDetail.calendar.buttons.today', 'Today'),
        month: t('offerDetail.calendar.buttons.month', 'Month'),
        week: t('offerDetail.calendar.buttons.week', 'Week'),
        day: t('offerDetail.calendar.buttons.day', 'Day'),
      },
      allDayText: t('offerDetail.calendar.allDay', 'All Day'),
      moreLinkText: t('offerDetail.calendar.more', 'more'),
      noEventsText: t('offerDetail.calendar.noEvents', 'No events'),
      weekText: t('offerDetail.calendar.week', 'Week'),
      dayNames: [
        t('offerDetail.calendar.days.sunday', 'Sunday'),
        t('offerDetail.calendar.days.monday', 'Monday'),
        t('offerDetail.calendar.days.tuesday', 'Tuesday'),
        t('offerDetail.calendar.days.wednesday', 'Wednesday'),
        t('offerDetail.calendar.days.thursday', 'Thursday'),
        t('offerDetail.calendar.days.friday', 'Friday'),
        t('offerDetail.calendar.days.saturday', 'Saturday'),
      ],
      dayNamesShort: [
        t('offerDetail.calendar.daysShort.sun', 'Sun'),
        t('offerDetail.calendar.daysShort.mon', 'Mon'),
        t('offerDetail.calendar.daysShort.tue', 'Tue'),
        t('offerDetail.calendar.daysShort.wed', 'Wed'),
        t('offerDetail.calendar.daysShort.thu', 'Thu'),
        t('offerDetail.calendar.daysShort.fri', 'Fri'),
        t('offerDetail.calendar.daysShort.sat', 'Sat'),
      ],
      monthNames: isFrench 
        ? ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
        : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      monthNamesShort: isFrench
        ? ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
        : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    }
  }, [t, i18n.language])

  // Filter events based on selected filters
  const filteredEvents = useMemo(() => {
    let filtered = events

    // Filter by offer (multi-select)
    if (offerFilter !== 'all' && Array.isArray(offerFilter) && offerFilter.length > 0) {
      filtered = filtered.filter((event) => {
        const eventOfferId = event.extendedProps?.offerId
        return eventOfferId !== undefined && offerFilter.includes(Number(eventOfferId))
      })
    }

    // Filter by candidate
    if (candidateFilter !== 'all') {
      filtered = filtered.filter((event) => {
        const eventCandidateId = event.candidateId || event.extendedProps?.candidateId
        return eventCandidateId !== undefined && Number(eventCandidateId) === Number(candidateFilter)
      })
    }

    // Filter by step
    if (stepFilter !== 'all') {
      filtered = filtered.filter((event) => {
        const eventStepOrder = event.stepOrder || event.extendedProps?.stepOrder
        return eventStepOrder !== undefined && Number(eventStepOrder) === Number(stepFilter)
      })
    }

    // Filter by group
    if (groupFilter !== 'all') {
      filtered = filtered.filter((event) => {
        const eventCandidateId = event.candidateId || event.extendedProps?.candidateId
        if (!eventCandidateId) return false
        const candidateGroupsList = candidateGroups.get(Number(eventCandidateId)) || []
        return candidateGroupsList.includes(groupFilter)
      })
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim()
      filtered = filtered.filter(
        (event) =>
          event.title?.toLowerCase().includes(term) ||
          event.extendedProps?.candidateName?.toLowerCase().includes(term) ||
          event.candidateName?.toLowerCase().includes(term) ||
          event.extendedProps?.location?.toLowerCase().includes(term) ||
          event.location?.toLowerCase().includes(term) ||
          event.extendedProps?.stepName?.toLowerCase().includes(term) ||
          event.stepName?.toLowerCase().includes(term) ||
          event.extendedProps?.offerTitle?.toLowerCase().includes(term)
      )
    }

    return filtered
  }, [events, offerFilter, candidateFilter, stepFilter, groupFilter, searchTerm, candidateGroups])

  // Transform events for FullCalendar
  const calendarEvents = useMemo(() => {
    return filteredEvents.map((event) => {
      // Get step color if available
      const stepOrder = event.stepOrder || event.extendedProps?.stepOrder
      const step = stepOrder !== undefined ? steps.find(s => s.order === stepOrder) : null
      const eventColor = step?.color || event.color || '#3B82F6'
      
      // Get offer info for visual differentiation
      const offerId = event.extendedProps?.offerId
      
      // Create a unique color based on offer ID for visual differentiation
      // Use vibrant, distinct colors for better visibility
      const getOfferColor = (offerId?: number) => {
        if (!offerId) return null
        // Generate a consistent, vibrant color based on offer ID
        const colors = [
          '#FF4444', '#FF8800', '#00CC66', '#0088FF', '#AA44FF', 
          '#FF44AA', '#00DDFF', '#FFDD00', '#FF0066', '#8844FF',
          '#00FF88', '#FF5500', '#5500FF', '#FFCC44', '#44CCFF',
          '#FF0099', '#0099FF', '#99FF44', '#FF8844', '#0066DD',
          '#FF3366', '#33FF66', '#3366FF', '#FF9933', '#9933FF'
        ]
        return colors[offerId % colors.length]
      }
      
      const offerColor = getOfferColor(offerId)
      const hasMultipleOffers = offers.length > 1
      
      // Keep original title without offer name
      const displayTitle = event.title
      
      return {
      id: String(event.id),
      title: displayTitle,
      start: event.start,
      end: event.end || event.start,
        backgroundColor: eventColor,
        borderColor: eventColor, // Keep border same as background
        borderWidth: 1,
        textColor: '#ffffff',
        classNames: hasMultipleOffers && offerId && offerColor ? [`offer-${offerId}`, 'offer-differentiated'] : [],
      extendedProps: {
        candidateId: event.candidateId || event.extendedProps?.candidateId,
        candidateName: event.candidateName || event.extendedProps?.candidateName,
        applicationId: event.applicationId || event.extendedProps?.applicationId,
          stepOrder: event.stepOrder || event.extendedProps?.stepOrder,
          stepName: event.stepName || event.extendedProps?.stepName,
        location: event.location || event.extendedProps?.location,
        meetingLink: event.meetingLink || event.extendedProps?.meetingLink,
        notes: event.notes || event.extendedProps?.notes,
        offerId: offerId,
        offerTitle: event.extendedProps?.offerTitle,
        offerColor: offerColor, // Store offer color for CSS styling
      },
  }
    })
  }, [filteredEvents, steps, offers.length])

  const clearFilters = () => {
    setCandidateFilter('all')
    setStepFilter('all')
    setGroupFilter('all')
    setOfferFilter('all')
    setSearchTerm('')
    if (onOfferFilterChange) {
      onOfferFilterChange('all')
    }
  }

  const handleOfferToggle = (offerId: number) => {
    const currentFilter = offerFilter === 'all' ? [] : (Array.isArray(offerFilter) ? offerFilter : [])
    const newFilter = currentFilter.includes(offerId)
      ? currentFilter.filter(id => id !== offerId)
      : [...currentFilter, offerId]
    
    const finalFilter = newFilter.length === 0 ? 'all' : newFilter
    setOfferFilter(finalFilter)
    if (onOfferFilterChange) {
      onOfferFilterChange(finalFilter)
    }
  }

  const handleSelectAllOffers = () => {
    setOfferFilter('all')
    if (onOfferFilterChange) {
      onOfferFilterChange('all')
    }
  }

  const hasActiveFilters = candidateFilter !== 'all' || stepFilter !== 'all' || groupFilter !== 'all' || (offerFilter !== 'all' && Array.isArray(offerFilter) && offerFilter.length > 0) || searchTerm.trim() !== ''

  // Get display text for offer filter
  const getOfferFilterDisplay = () => {
    if (offerFilter === 'all') {
      return t('offerDetail.calendar.allOffers', 'All Offers')
    }
    if (Array.isArray(offerFilter) && offerFilter.length === 0) {
      return t('offerDetail.calendar.allOffers', 'All Offers')
    }
    if (Array.isArray(offerFilter) && offerFilter.length === 1) {
      const offer = offers.find(o => o.id === offerFilter[0])
      return offer ? offer.name : t('offerDetail.calendar.filterByOffer', 'Filter by offer')
    }
    if (Array.isArray(offerFilter) && offerFilter.length > 1) {
      return `${offerFilter.length} ${t('offerDetail.calendar.selectedOffers', 'offers selected')}`
    }
    return t('offerDetail.calendar.filterByOffer', 'Filter by offer')
  }

  // Sort filtered events by date for list view
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      const dateA = new Date(a.start).getTime()
      const dateB = new Date(b.start).getTime()
      return dateA - dateB
    })
  }, [filteredEvents])

  const formatDateTime = (date: Date | string) => {
    const d = new Date(date)
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const formatTime = (date: Date | string) => {
    const d = new Date(date)
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const getEventColor = (event: InterviewEvent) => {
    const stepOrder = event.stepOrder || event.extendedProps?.stepOrder
    const step = stepOrder !== undefined ? steps.find(s => s.order === stepOrder) : null
    return step?.color || event.color || '#3B82F6'
  }

  return (
    <div className="space-y-6">
      {hideHeader ? (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <div className="flex items-center gap-2 border rounded-lg p-1">
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className="gap-2"
            >
              <Grid className="h-4 w-4" />
              {t('offerDetail.calendar.calendarView', 'Calendar')}
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-2"
            >
              <List className="h-4 w-4" />
              {t('offerDetail.calendar.listView', 'List')}
            </Button>
          </div>
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="gap-2 hover:bg-destructive/10 hover:border-destructive/20 hover:text-destructive transition-all"
            >
              <X className="h-4 w-4" />
              {t('common.clearFilters', 'Clear Filters')}
            </Button>
          )}
          <Badge variant="secondary" className="text-sm font-semibold">
            <Clock className="h-3 w-3 mr-1" />
            {filteredEvents.length} {t('offerDetail.calendar.interviews', 'interviews')}
          </Badge>
        </div>
      ) : (
        <PageHeader
          title={t('offerDetail.calendar.title', 'Interview Calendar')}
          description={t('offerDetail.calendar.subtitle', 'Manage and schedule your interviews')}
          actions={
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 border rounded-lg p-1">
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className="gap-2"
                >
                  <Grid className="h-4 w-4" />
                  {t('offerDetail.calendar.calendarView', 'Calendar')}
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="gap-2"
                >
                  <List className="h-4 w-4" />
                  {t('offerDetail.calendar.listView', 'List')}
                </Button>
              </div>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-2 hover:bg-destructive/10 hover:border-destructive/20 hover:text-destructive transition-all"
                >
                  <X className="h-4 w-4" />
                  {t('common.clearFilters', 'Clear Filters')}
                </Button>
              )}
              <Badge variant="secondary" className="text-sm font-semibold">
                <Clock className="h-3 w-3 mr-1" />
                {filteredEvents.length} {t('offerDetail.calendar.interviews', 'interviews')}
              </Badge>
            </div>
          }
        />
      )}

      {/* Filters Section */}
      <Card className="border shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative sm:min-w-[200px] sm:max-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('offerDetail.calendar.searchPlaceholder', 'Search interviews...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10"
            />
          </div>
          {offers.length > 0 && (
              <div className="sm:flex-1 sm:min-w-[350px] sm:max-w-[450px] relative" ref={offerFilterRef}>
              <button
                type="button"
                onClick={() => setOfferFilterOpen(!offerFilterOpen)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <span className="whitespace-nowrap overflow-hidden text-ellipsis block min-w-0 flex-1 text-left">
                  {getOfferFilterDisplay()}
                </span>
                <ChevronDown className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ml-2",
                  offerFilterOpen && "rotate-180"
                )} />
              </button>

              {offerFilterOpen && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-input rounded-md shadow-lg max-h-60 overflow-auto">
                  <button
                    type="button"
                    onClick={handleSelectAllOffers}
                    className={cn(
                      "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none",
                      offerFilter === 'all' && "bg-accent text-accent-foreground"
                    )}
                  >
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis block min-w-0 flex-1 text-left">
                      {t('offerDetail.calendar.allOffers', 'All Offers')}
                    </span>
                    {offerFilter === 'all' && (
                      <Check className="h-4 w-4 flex-shrink-0" />
                    )}
                  </button>
                  {offers.map((offer) => {
                    const isSelected = Array.isArray(offerFilter) && offerFilter.includes(offer.id)
                    return (
                      <button
                        key={offer.id}
                        type="button"
                        onClick={() => handleOfferToggle(offer.id)}
                        className={cn(
                          "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none",
                          isSelected && "bg-accent text-accent-foreground"
                        )}
                      >
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis block min-w-0 flex-1 text-left">
                          {offer.name}
                        </span>
                        {isSelected && (
                          <Check className="h-4 w-4 flex-shrink-0" />
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          {candidates.length > 0 && (
              <div className="sm:min-w-[180px]">
              <CustomSelect
                value={candidateFilter === 'all' ? 'all' : String(candidateFilter)}
                onChange={(value) => setCandidateFilter(value === 'all' ? 'all' : Number(value))}
                options={[
                  { value: 'all', label: t('offerDetail.calendar.allCandidates', 'All Candidates') },
                  ...candidates.map((c) => ({ value: String(c.id), label: c.name })),
                ]}
                placeholder={t('offerDetail.calendar.filterByCandidate', 'Filter by candidate')}
              />
            </div>
          )}
            {steps.length > 0 && (() => {
              // Filter: show steps from order 3 onwards, but exclude rejected
              const visibleSteps = steps.filter(step => {
                // Must be at least order 3
                if (step.order < 3) {
                  return false
                }
                
                // Exclude rejected step (by step_type or name)
                if (step.step_type === 'rejected') {
                  return false
                }
                const stepNameLower = step.name.toLowerCase()
                if (stepNameLower.includes('rejected') || stepNameLower.includes('reject')) {
                  return false
                }
                
                return true
              }).sort((a, b) => a.order - b.order)

              return visibleSteps.length > 0 ? (
                <div className="sm:min-w-[180px] relative" ref={stepFilterRef}>
                  <button
                    type="button"
                    onClick={() => setStepFilterOpen(!stepFilterOpen)}
                    className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis block min-w-0 flex-1 text-left">
                      {stepFilter === 'all' 
                        ? t('offerDetail.calendar.allSteps', 'All Steps')
                        : (() => {
                            const selectedStep = visibleSteps.find(s => s.order === stepFilter)
                            return selectedStep ? (
                              <span className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded border border-border/50 flex-shrink-0"
                                  style={{ backgroundColor: selectedStep.color || '#3B82F6' }}
                                />
                                {selectedStep.name}
                              </span>
                            ) : t('offerDetail.calendar.filterByStep', 'Filter by step')
                          })()}
                    </span>
                    <ChevronDown className={cn(
                      "h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ml-2",
                      stepFilterOpen && "rotate-180"
                    )} />
                  </button>

                  {stepFilterOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-background border border-input rounded-md shadow-lg max-h-60 overflow-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setStepFilter('all')
                          setStepFilterOpen(false)
                        }}
                        className={cn(
                          "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none",
                          stepFilter === 'all' && "bg-accent text-accent-foreground"
                        )}
                      >
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis block min-w-0 flex-1 text-left">
                          {t('offerDetail.calendar.allSteps', 'All Steps')}
                        </span>
                        {stepFilter === 'all' && (
                          <Check className="h-4 w-4 flex-shrink-0" />
                        )}
                      </button>
                      {visibleSteps.map((step) => {
                        const isSelected = stepFilter === step.order
                        return (
                          <button
                            key={step.id}
                            type="button"
                            onClick={() => {
                              setStepFilter(step.order)
                              setStepFilterOpen(false)
                            }}
                            className={cn(
                              "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none",
                              isSelected && "bg-accent text-accent-foreground"
                            )}
                          >
                            <span className="flex items-center gap-2 whitespace-nowrap overflow-hidden text-ellipsis block min-w-0 flex-1 text-left">
                              <div
                                className="w-3 h-3 rounded border border-border/50 flex-shrink-0"
                                style={{ backgroundColor: step.color || '#3B82F6' }}
                              />
                              {step.name}
                            </span>
                            {isSelected && (
                              <Check className="h-4 w-4 flex-shrink-0" />
                            )}
                          </button>
                        )
                      })}
            </div>
          )}
                </div>
              ) : null
            })()}
          {groups.length > 0 && (
            <div className="sm:min-w-[180px]">
              <CustomSelect
                value={groupFilter === 'all' ? 'all' : groupFilter}
                onChange={(value) => setGroupFilter(value === 'all' ? 'all' : value)}
                options={[
                  { value: 'all', label: t('offerDetail.calendar.allGroups', 'All Groups') },
                  ...groups.map((g) => ({ value: g.name, label: g.name })),
                ]}
                placeholder={t('offerDetail.calendar.filterByGroup', 'Filter by group')}
              />
            </div>
          )}
        </div>
        </CardContent>
      </Card>

      {/* Calendar/List Section */}
      <Card className="border shadow-lg overflow-hidden">
        <CardContent className="p-0">
        {loading ? (
            <div className="flex items-center justify-center py-20">
            <div className="text-center">
                <div className="relative">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary mx-auto"></div>
                  <Calendar className="h-6 w-6 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                </div>
                <p className="mt-4 text-sm font-medium text-muted-foreground">{t('common.loading', 'Loading...')}</p>
            </div>
          </div>
        ) : viewMode === 'calendar' ? (
            <div className="interview-calendar-wrapper">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={view}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              locale={calendarLocale}
              buttonText={calendarLocale.buttonText}
              selectable={true}
              editable={true}
              events={calendarEvents}
              select={onSlotSelect}
              eventClick={(arg) => {
                const event = arg.event
                const interview = events.find(i => String(i.id) === event.id)
                if (interview) {
                  setSelectedInterview(interview)
                  setShowInterviewModal(true)
                }
                // Also call the parent's onEventClick if provided
                if (onEventClick) {
                  onEventClick(arg)
                }
              }}
              eventDrop={onEventDrop}
              slotMinTime="08:00:00"
                slotMaxTime="18:00:00"
              height="auto"
              firstDay={1}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }}
                dayHeaderFormat={{ weekday: 'short' }}
              views={{
                dayGridMonth: {
                  dayMaxEvents: 3,
                    moreLinkClick: 'popover',
                  displayEventTime: true,
                  eventTimeFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  },
                },
                timeGridWeek: {
                  slotDuration: '00:30:00',
                    allDaySlot: false,
                },
                timeGridDay: {
                  slotDuration: '00:30:00',
                    allDaySlot: false,
                },
              }}
                eventDisplay="block"
                eventClassNames="interview-event"
                dayCellClassNames="calendar-day-cell"
                moreLinkClassNames="calendar-more-link"
                eventContent={(arg) => {
                  const event = arg.event
                  const offerColor = event.extendedProps?.offerColor
                  
                  // Custom rendering ONLY for month view to ensure time displays properly
                  if (arg.view.type === 'dayGridMonth') {
                    const start = event.start
                    const timeStr = start ? new Date(start).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    }) : ''
                    const title = event.title || ''
                    
                    // Add colored left border indicator
                    const borderStyle = offerColor ? `style="border-left: 6px solid ${offerColor}; padding-left: 0.5rem;"` : ''
                    
                    return {
                      html: `<div class="fc-event-main-frame" ${borderStyle}>
                        <div class="fc-event-time-container">
                          <span class="fc-event-time">${timeStr}</span>
                        </div>
                        <div class="fc-event-title-container">
                          <span class="fc-event-title">${title}</span>
                        </div>
                      </div>`
                    }
                  }
                  // For week and day views, return the default content structure with colored border
                  const borderStyle = offerColor ? `style="border-left: 6px solid ${offerColor}; padding-left: 0.5rem;"` : ''
                  
                  return {
                    html: arg.timeText 
                      ? `<div class="fc-event-main-frame" ${borderStyle}>
                          <div class="fc-event-time-container">
                            <span class="fc-event-time">${arg.timeText}</span>
                          </div>
                          <div class="fc-event-title-container">
                            <span class="fc-event-title">${arg.event.title}</span>
                          </div>
                        </div>`
                      : `<div class="fc-event-main-frame" ${borderStyle}>
                          <div class="fc-event-title-container">
                            <span class="fc-event-title">${arg.event.title}</span>
                          </div>
                        </div>`
                  }
                }}
            />
          </div>
        ) : (
          <div className="p-6">
            {sortedEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">
                  {t('offerDetail.calendar.noInterviews', 'No interviews found')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {hasActiveFilters
                    ? t('offerDetail.calendar.noInterviewsFiltered', 'Try adjusting your filters')
                    : t('offerDetail.calendar.noInterviewsEmpty', 'No interviews scheduled yet')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedEvents.map((event) => {
                  const eventColor = getEventColor(event)
                  const startDate = event.start ? new Date(event.start) : null
                  const endDate = event.end ? new Date(event.end) : null
                  
                  return (
                    <div
                      key={event.id}
                      onClick={() => {
                        setSelectedInterview(event)
                        setShowInterviewModal(true)
                      }}
                      className="flex items-start gap-4 p-4 rounded-lg border border-border hover:border-primary/50 hover:shadow-md transition-all cursor-pointer bg-card"
                    >
                      {/* Color indicator */}
                      <div
                        className="w-1 h-full min-h-[60px] rounded-full flex-shrink-0"
                        style={{ backgroundColor: eventColor }}
                      />
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-foreground mb-1 truncate">
                              {event.title}
                            </h3>
                            {(event.candidateName || event.extendedProps?.candidateName) && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {event.candidateName || event.extendedProps?.candidateName}
                              </p>
                            )}
                          </div>
                          {(event.stepName || event.extendedProps?.stepName) && (
                            <Badge
                              variant="secondary"
                              className="flex-shrink-0"
                              style={{
                                backgroundColor: `${eventColor}20`,
                                color: eventColor,
                                borderColor: `${eventColor}40`,
                              }}
                            >
                              {event.stepName || event.extendedProps?.stepName}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          {startDate && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Clock className="h-4 w-4 flex-shrink-0" />
                              <span>
                                {formatDateTime(startDate)}
                                {endDate && ` - ${formatTime(endDate)}`}
                              </span>
                            </div>
                          )}
                          {(event.location || event.extendedProps?.location) && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="h-4 w-4 flex-shrink-0" />
                              <span className="truncate">
                                {event.location || event.extendedProps?.location}
                              </span>
                            </div>
                          )}
                          {(event.meetingLink || event.extendedProps?.meetingLink) && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <LinkIcon className="h-4 w-4 flex-shrink-0" />
                              <a
                                href={event.meetingLink || event.extendedProps?.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-primary hover:underline truncate"
                              >
                                {event.meetingLink || event.extendedProps?.meetingLink}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>

      {/* Interview Details Modal */}
      <InterviewDetailsModal
        isOpen={showInterviewModal}
        onClose={() => {
          setShowInterviewModal(false)
          setSelectedInterview(null)
        }}
        interview={selectedInterview}
      />
    </div>
  )
}

